import {
  DEFAULT_EXAMPLE_SKILL_NAME,
  OPENCLAW_SKILL_ROOT,
  OPENAI_YAML_SUBPATH,
  SkillCollectionFileName,
} from "./constants";
import type { SkillCollectionPreset } from "./skill-presets";
import {
  AGENTS_MD_DEFAULT,
  CLAUDE_MD_DEFAULT,
  CLAUDE_SKILL_EXAMPLE,
  OPENCLAW_AGENTS_DEFAULT,
  OPENCLAW_AGENTS_SCAFFOLD,
  OPENCLAW_BOOTSTRAP_DEFAULT,
  OPENCLAW_GITIGNORE_DEFAULT,
  OPENCLAW_HEARTBEAT_DEFAULT,
  OPENCLAW_IDENTITY_DEFAULT,
  OPENCLAW_IDENTITY_SCAFFOLD,
  OPENCLAW_SKILL_EXAMPLE,
  OPENCLAW_SOUL_DEFAULT,
  OPENCLAW_SOUL_SCAFFOLD,
  OPENCLAW_TOOLS_DEFAULT,
  OPENCLAW_TOOLS_SCAFFOLD,
  OPENCLAW_USER_DEFAULT,
  OPENCLAW_USER_SCAFFOLD,
  OPENAI_SKILL_EXAMPLE,
  OPENAI_YAML_EXAMPLE,
} from "./provider-default-content";

interface ProviderFileLimit {
  match: (relativePath: string) => boolean;
  recommendedChars: number;
  label: string;
}

export interface ProviderFile {
  path: string;
  content: string;
}

export interface SkillInternalFile {
  subPath: string;
  getContent: (meta: SkillMeta) => string;
}

export interface SkillMeta {
  name: string;
  description: string;
  disableModelInvocation: boolean;
}

export interface ProviderConfig {
  displayName: string;
  logoProvider: string;
  configFile: string;
  skillRoot: string | null;
  protectedPaths: string[];
  fileLimits: ProviderFileLimit[];
  importPrefixes: string[];
  presetFiles: ProviderFile[];
  scaffoldFiles: ProviderFile[];
  skillInternalFiles: SkillInternalFile[];
  infrastructurePaths: string[];
}

const isFileName = (relativePath: string, name: string): boolean =>
  relativePath.split("/").pop()?.toUpperCase() === name.toUpperCase();

const buildOpenaiYaml = (meta: SkillMeta) => `interface:
  display_name: "${meta.name}"
  short_description: "${meta.description}"

policy:
  allow_implicit_invocation: ${!meta.disableModelInvocation}
`;

const OPENCLAW_PROTECTED_FILES = [
  SkillCollectionFileName.AgentsMd,
  "SOUL.md",
  "USER.md",
  "BOOTSTRAP.md",
  "HEARTBEAT.md",
  "IDENTITY.md",
  "TOOLS.md",
  ".gitignore",
];

const OPENCLAW_INFRASTRUCTURE_FILES = OPENCLAW_PROTECTED_FILES.filter(
  path => path !== SkillCollectionFileName.AgentsMd,
);

export const PROVIDER_CONFIGS: Record<SkillCollectionPreset, ProviderConfig> = {
  claude_code: {
    displayName: "Claude Code",
    logoProvider: "anthropic",
    configFile: SkillCollectionFileName.ClaudeMd,
    skillRoot: null,
    protectedPaths: [SkillCollectionFileName.ClaudeMd],
    importPrefixes: [".claude/skills/", ".claude/"],
    fileLimits: [
      {
        match: p => p.toUpperCase() === SkillCollectionFileName.ClaudeMd.toUpperCase(),
        recommendedChars: 40_000,
        label: "\u226440k chars recommended",
      },
      {
        match: p => isFileName(p, SkillCollectionFileName.SkillMd),
        recommendedChars: 8_000,
        label: "~1,500\u20132,000 words",
      },
    ],
    presetFiles: [
      { path: SkillCollectionFileName.ClaudeMd, content: CLAUDE_MD_DEFAULT },
      { path: `example/${SkillCollectionFileName.SkillMd}`, content: CLAUDE_SKILL_EXAMPLE },
    ],
    scaffoldFiles: [],
    skillInternalFiles: [],
    infrastructurePaths: [],
  },

  openai: {
    displayName: "OpenAI",
    logoProvider: "openai",
    configFile: SkillCollectionFileName.AgentsMd,
    skillRoot: null,
    protectedPaths: [SkillCollectionFileName.AgentsMd],
    importPrefixes: [".agents/skills/", ".agents/"],
    fileLimits: [
      {
        match: p => p.toUpperCase() === SkillCollectionFileName.AgentsMd.toUpperCase(),
        recommendedChars: 32_768,
        label: "32 KB default limit",
      },
      {
        match: p => isFileName(p, SkillCollectionFileName.SkillMd),
        recommendedChars: 8_000,
        label: "~1,500\u20132,000 words",
      },
    ],
    presetFiles: [
      { path: SkillCollectionFileName.AgentsMd, content: AGENTS_MD_DEFAULT },
      {
        path: `${DEFAULT_EXAMPLE_SKILL_NAME}/${SkillCollectionFileName.SkillMd}`,
        content: OPENAI_SKILL_EXAMPLE,
      },
      {
        path: `${DEFAULT_EXAMPLE_SKILL_NAME}/${OPENAI_YAML_SUBPATH}`,
        content: OPENAI_YAML_EXAMPLE,
      },
    ],
    scaffoldFiles: [],
    skillInternalFiles: [{ subPath: OPENAI_YAML_SUBPATH, getContent: buildOpenaiYaml }],
    infrastructurePaths: [],
  },

  openclaw: {
    displayName: "OpenClaw",
    logoProvider: "openclaw",
    configFile: SkillCollectionFileName.AgentsMd,
    skillRoot: OPENCLAW_SKILL_ROOT,
    protectedPaths: OPENCLAW_PROTECTED_FILES,
    importPrefixes: [".openclaw/skills/", ".openclaw/"],
    fileLimits: [
      {
        match: p => p.toLowerCase() === SkillCollectionFileName.AgentsMd.toLowerCase(),
        recommendedChars: 32_768,
        label: "~32k chars guideline",
      },
      {
        match: p => isFileName(p, SkillCollectionFileName.SkillMd),
        recommendedChars: 8_000,
        label: "~1,500\u20132,000 words",
      },
    ],
    presetFiles: [
      { path: SkillCollectionFileName.AgentsMd, content: OPENCLAW_AGENTS_DEFAULT },
      { path: "SOUL.md", content: OPENCLAW_SOUL_DEFAULT },
      { path: "USER.md", content: OPENCLAW_USER_DEFAULT },
      { path: "BOOTSTRAP.md", content: OPENCLAW_BOOTSTRAP_DEFAULT },
      { path: "HEARTBEAT.md", content: OPENCLAW_HEARTBEAT_DEFAULT },
      { path: "IDENTITY.md", content: OPENCLAW_IDENTITY_DEFAULT },
      { path: "TOOLS.md", content: OPENCLAW_TOOLS_DEFAULT },
      { path: ".gitignore", content: OPENCLAW_GITIGNORE_DEFAULT },
      {
        path: `${OPENCLAW_SKILL_ROOT}/${DEFAULT_EXAMPLE_SKILL_NAME}/${SkillCollectionFileName.SkillMd}`,
        content: OPENCLAW_SKILL_EXAMPLE,
      },
    ],
    scaffoldFiles: [
      { path: SkillCollectionFileName.AgentsMd, content: OPENCLAW_AGENTS_SCAFFOLD },
      { path: "SOUL.md", content: OPENCLAW_SOUL_SCAFFOLD },
      { path: "USER.md", content: OPENCLAW_USER_SCAFFOLD },
      { path: "TOOLS.md", content: OPENCLAW_TOOLS_SCAFFOLD },
      { path: "IDENTITY.md", content: OPENCLAW_IDENTITY_SCAFFOLD },
    ],
    skillInternalFiles: [],
    infrastructurePaths: OPENCLAW_INFRASTRUCTURE_FILES,
  },
};

export interface FileLimitInfo {
  provider: string;
  recommendedChars: number;
  label: string;
}

export const getFileLimitForProvider = (
  provider: SkillCollectionPreset | null | undefined,
  relativePath: string,
): FileLimitInfo | null => {
  if (!provider) {
    return null;
  }

  const config = PROVIDER_CONFIGS[provider];
  const rule = config.fileLimits.find(limit => limit.match(relativePath));

  if (!rule) {
    return null;
  }

  return {
    provider: config.displayName,
    recommendedChars: rule.recommendedChars,
    label: rule.label,
  };
};

export const ALL_IMPORT_PREFIXES: string[] = Object.values(PROVIDER_CONFIGS)
  .flatMap(c => c.importPrefixes)
  .sort((a, b) => b.length - a.length);

export const detectProviderFromRawPaths = (rawPaths: string[]): SkillCollectionPreset | null => {
  const presets = Object.keys(PROVIDER_CONFIGS) as SkillCollectionPreset[];

  for (const preset of presets) {
    const { importPrefixes } = PROVIDER_CONFIGS[preset];
    if (rawPaths.some(p => importPrefixes.some(prefix => p.includes(prefix)))) {
      return preset;
    }
  }

  return null;
};
